'use client'

import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { createFeedback, type FeedbackCategory } from '@/lib/feedback'
import { useLocale, useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { IconMessageCircle, IconCheck, IconLoader2 } from '@tabler/icons-react'
import { cn } from '@/lib/utils'

// =============================================================================
// SPAM PROTECTION
// =============================================================================

const RATE_LIMIT_KEY = 'miaozi_feedback_submissions'
const MAX_SUBMISSIONS_PER_HOUR = 5
const MIN_FORM_TIME_MS = 2000 // Minimum 2 seconds (dialog is simpler than contact form)

interface RateLimitData {
  timestamps: number[]
}

function getRateLimitData(): RateLimitData {
  if (typeof window === 'undefined') return { timestamps: [] }
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY)
    if (!stored) return { timestamps: [] }
    return JSON.parse(stored)
  } catch {
    return { timestamps: [] }
  }
}

function isRateLimited(): boolean {
  const data = getRateLimitData()
  const oneHourAgo = Date.now() - 60 * 60 * 1000
  const recentSubmissions = data.timestamps.filter((ts) => ts > oneHourAgo)
  return recentSubmissions.length >= MAX_SUBMISSIONS_PER_HOUR
}

function recordSubmission(): void {
  if (typeof window === 'undefined') return
  const data = getRateLimitData()
  const oneHourAgo = Date.now() - 60 * 60 * 1000
  data.timestamps = [
    ...data.timestamps.filter((ts) => ts > oneHourAgo),
    Date.now()
  ]
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data))
}

// =============================================================================
// COMPONENT
// =============================================================================

interface FeedbackDialogProps {
  children?: React.ReactNode
  variant?: 'default' | 'compact'
}

export function FeedbackDialog({
  children,
  variant = 'default'
}: FeedbackDialogProps) {
  const t = useTranslations('feedback')
  const tCommon = useTranslations('common')
  const locale = useLocale()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState<FeedbackCategory>('feedback')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track when dialog opens for timing validation
  const dialogOpenTime = useRef<number>(Date.now())

  // Reset timer when dialog opens
  useEffect(() => {
    if (open) {
      dialogOpenTime.current = Date.now()
      setError(null)
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.id || !message.trim()) return

    setError(null)

    // Timing check: form filled too fast = suspicious
    const timeSpent = Date.now() - dialogOpenTime.current
    if (timeSpent < MIN_FORM_TIME_MS) {
      // Silently succeed for bots
      setIsSuccess(true)
      setTimeout(() => {
        setOpen(false)
        setTimeout(() => {
          setMessage('')
          setCategory('feedback')
          setIsSuccess(false)
        }, 200)
      }, 1500)
      return
    }

    // Rate limiting check
    if (isRateLimited()) {
      setError('Too many submissions. Please try again later.')
      return
    }

    setIsSubmitting(true)
    try {
      await createFeedback({
        category,
        message: message.trim(),
        userId: user.id,
        email: user.email,
        locale,
        meta: {
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : undefined
        }
      })
      recordSubmission()
      setIsSuccess(true)
      setTimeout(() => {
        setOpen(false)
        // Reset after close animation
        setTimeout(() => {
          setMessage('')
          setCategory('feedback')
          setIsSuccess(false)
        }, 200)
      }, 1500)
    } catch (err) {
      console.error('Failed to submit feedback:', err)
      setError('Failed to send feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Only show for logged-in users
  if (!user) return null

  const trigger = children || (
    <Button
      variant="ghost"
      size={variant === 'compact' ? 'icon' : 'sm'}
      className={cn('gap-2', variant === 'compact' && 'h-9 w-9')}
    >
      <IconMessageCircle className="h-4 w-4" />
      {variant !== 'compact' && (
        <span className="hidden sm:inline">{t('title')}</span>
      )}
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400">
              <IconCheck className="h-6 w-6" />
            </div>
            <p className="text-lg font-medium">{t('thankYou')}</p>
            <p className="text-muted-foreground text-sm">{t('received')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('category')}</label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as FeedbackCategory)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feedback">
                    {t('categories.feedback')}
                  </SelectItem>
                  <SelectItem value="bug">{t('categories.bug')}</SelectItem>
                  <SelectItem value="feature">
                    {t('categories.feature')}
                  </SelectItem>
                  <SelectItem value="other">{t('categories.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('message')}</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('messagePlaceholder')}
                rows={4}
                required
                className="resize-none"
              />
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || !message.trim()}>
                {isSubmitting ? (
                  <>
                    <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  t('send')
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
